<script>
    import { createEventDispatcher } from "svelte";
    import { writable, get } from "svelte/store";
    import {
        describeCronExpression,
        CRON_HELP_LINES,
        CRON_HELP_EXAMPLES
    } from "$utils/cronDescription.js";
    import { createBaseJobData, buildJobDataForJobId, templateVariablesHint, recipientTypeHint } from "./jobDefaults.js";

    const dispatch = createEventDispatcher();
    export let showForm = false;
    export let editJob = null;
    export let availableJobIds = [];

    let showCronHelp = false;

    let jobData = writable(createBaseJobData());

    $: placeholdersHint = templateVariablesHint($jobData.jobId);
    $: recipientHint = recipientTypeHint($jobData.recipientType);

    $: if (editJob) {
        const jobToEdit = {
            ...editJob,
            parameters: typeof editJob.parameters === 'string' 
                ? editJob.parameters 
                : JSON.stringify(editJob.parameters || {}, null, 2)
        };
        jobData.set(jobToEdit);
    }

    $: if (showForm && !editJob) {
        resetJobData();
    }

    function resetJobData() {
        jobData.set(createBaseJobData());
    }

    function handleJobIdSelectionChange(event) {
        if (editJob) return;
        jobData.set(buildJobDataForJobId(event.currentTarget.value));
    }

    function close() {
        showCronHelp = false;
        dispatch("close");
    }

    async function handleSubmit() {
        const job = get(jobData);
        try {
            const parameters = JSON.parse(job.parameters);
            const jobToSubmit = {
                ...job,
                parameters
            };
            dispatch("submit", jobToSubmit);
            close();
        } catch (e) {
            console.error('Error parsing parameters JSON:', e);
            // You might want to show an error message to the user here
        }
    }
</script>

{#if showForm}
    <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
        <button
            type="button"
            class="modal-overlay absolute inset-0 bg-slate-800 opacity-75"
            on:click={close}
            aria-label="Close job form"
        ></button>
        <div class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-3xl relative z-60">
            <header class="flex justify-between items-start mb-4">
                <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {editJob ? "Edit Job" : "New Job"}
                </h2>
                <button
                    on:click={close}
                    class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                >
                    &times;
                </button>
            </header>
            <form on:submit|preventDefault={handleSubmit} class="space-y-4">
                <div class="flex gap-3 pt-4">
                    <div class="w-1/2">
                        <label for="scheduler-job-id" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Job ID *</label>
                        {#if editJob || !availableJobIds?.length}
                            <input
                                id="scheduler-job-id"
                                type="text"
                                bind:value={$jobData.jobId}
                                disabled={editJob}
                                class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                required
                            />
                        {:else}
                            <select
                                id="scheduler-job-id"
                                bind:value={$jobData.jobId}
                                on:change={handleJobIdSelectionChange}
                                class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 bg-white"
                                required
                            >
                                <option value="" disabled>-- Select a job --</option>
                                {#each availableJobIds as id (id)}
                                    <option value={id}>{id}</option>
                                {/each}
                            </select>
                        {/if}
                    </div>
                    <div class="w-1/2">
                        <label for="scheduler-variant-name" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Variant Name *</label>
                        <input
                            id="scheduler-variant-name"
                            type="text"
                            bind:value={$jobData.variantName}
                            disabled={editJob}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                            placeholder="default"
                            required
                        />
                    </div>
                </div>
                <div class="flex gap-3">
                    <div class="w-3/4 relative">
                        <div class="flex items-center gap-2 pb-1">
                            <label for="scheduler-cron-schedule" class="block text-xs text-gray-700 dark:text-gray-200">
                                Cron Schedule *
                            </label>
                            <button
                                type="button"
                                class="inline-flex h-5 w-5 items-center justify-center rounded-full border border-blue-400 text-[11px] font-semibold text-blue-500 hover:bg-blue-50 dark:border-blue-500 dark:text-blue-300 dark:hover:bg-blue-950/30"
                                title={showCronHelp ? 'Hide cron help' : 'Show cron help'}
                                aria-label={showCronHelp ? 'Hide cron help' : 'Show cron help'}
                                aria-expanded={showCronHelp}
                                on:click={() => (showCronHelp = !showCronHelp)}
                            >
                                i
                            </button>
                        </div>
                        <input
                            id="scheduler-cron-schedule"
                            type="text"
                            bind:value={$jobData.cronSchedule}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                            required
                        />
                        <div class="pt-2 text-xs text-gray-600 dark:text-gray-300">
                            Preview: <span class="font-medium text-gray-800 dark:text-gray-100">{describeCronExpression($jobData.cronSchedule)}</span>
                        </div>
                        {#if showCronHelp}
                            <div class="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                                <div class="font-semibold text-gray-900 dark:text-gray-100">Cron format</div>
                                <div class="pt-1 font-mono text-[11px] text-gray-700 dark:text-gray-300">minute hour day-of-month month weekday</div>
                                <div class="space-y-1 pt-2">
                                    {#each CRON_HELP_LINES as line (line)}
                                        <div>{line}</div>
                                    {/each}
                                </div>
                                <div class="pt-3 font-semibold text-gray-900 dark:text-gray-100">Examples</div>
                                <div class="space-y-1 pt-1">
                                    {#each CRON_HELP_EXAMPLES as example (example.cron)}
                                        <div>
                                            <span class="font-mono text-[11px] text-gray-800 dark:text-gray-100">{example.cron}</span>
                                            <span class="text-gray-500 dark:text-gray-400"> - {example.description}</span>
                                        </div>
                                    {/each}
                                </div>
                            </div>
                        {/if}
                    </div>
                    <div class="w-1/4">
                        <label for="scheduler-enabled" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Enabled</label>
                        <select
                            id="scheduler-enabled"
                            bind:value={$jobData.enabled}
                            class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                        >
                            <option value={true}>Yes</option>
                            <option value={false}>No</option>
                        </select>
                    </div>
                </div>
                <div class="flex gap-3">
                    <div class="w-3/4">
                        <label for="scheduler-message-title" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Message Title</label>
                        <p class="pb-1 text-[11px] text-gray-500 dark:text-gray-400">{placeholdersHint}</p>
                        <input
                            id="scheduler-message-title"
                            type="text"
                            bind:value={$jobData.messageTitle}
                            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                            placeholder="Enter message title"
                        />
                    </div>
                    <div class="w-1/4">
                        <label for="scheduler-recipient-type" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Recipients</label>
                        <p class="pb-1 text-[11px] text-gray-500 dark:text-gray-400 truncate">{recipientHint}</p>
                        <select
                            id="scheduler-recipient-type"
                            bind:value={$jobData.recipientType}
                            class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                        >
                            <option value="participant">Participant</option>
                            <option value="site">Site</option>
                            <option value="both">Both</option>
                        </select>
                    </div>
                </div>
                <div class="flex gap-3">
                    <div class="w-full">
                        <label for="scheduler-message" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Message</label>
                        <p class="pb-1 text-[11px] text-gray-500 dark:text-gray-400">{placeholdersHint}</p>
                        <textarea
                            id="scheduler-message"
                            bind:value={$jobData.message}
                            class="border p-1 w-full rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                            rows="4"
                            placeholder="Enter message content"
                        ></textarea>
                    </div>
                </div>
                <div class="flex gap-3">
                    <div class="w-full">
                        <label for="scheduler-parameters" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Parameters (JSON)</label>
                        <textarea
                            id="scheduler-parameters"
                            bind:value={$jobData.parameters}
                            class="border p-1 w-full rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 font-mono"
                            rows="4"
                        ></textarea>
                    </div>
                </div>
                <div class="flex gap-3">
                    <div class="w-full">
                        <label for="scheduler-description" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Description</label>
                        <textarea
                            id="scheduler-description"
                            bind:value={$jobData.description}
                            class="border p-1 w-full rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                            rows="3"
                            placeholder="Enter job description"
                        ></textarea>
                    </div>
                </div>
                <div class="flex justify-end space-x-2 pt-10">
                    <button
                        type="button"
                        on:click={close}
                        class="bg-gray-500 text-white py-1 px-2 rounded hover:bg-gray-700"
                    >Cancel</button>
                    <button
                        type="submit"
                        class="bg-blue-500 text-white py-1 px-2 rounded hover:bg-blue-700"
                    >{editJob ? 'Save' : 'Create'}</button>
                </div>
            </form>
        </div>
    </div>
{/if} 